import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BaseFieldProps, CascadingOption } from '../types'
import { cn } from '@/lib/utils'
import { Path } from 'react-hook-form'
import { SingleLevelCombobox } from './SingleLevelCombobox'
import { MultiLevelCombobox } from './MultiLevelCombobox'

interface CascadingFieldValue {
  [key: string]: string | string[] | unknown
}

// Helper function to infer levels from options structure
function inferLevelsFromOptions(options: CascadingOption[] | undefined): string[] {
  const levels: string[] = ['level1']

  if (!Array.isArray(options) || options.length === 0) {
    return levels
  }

  // Check for level2
  const firstOption = options[0]
  if (Array.isArray(firstOption.options) && firstOption.options.length > 0) {
    levels.push('level2')

    // Check for level3
    const secondOption = firstOption.options[0]
    if (Array.isArray(secondOption.options) && secondOption.options.length > 0) {
      levels.push('level3')

      // Check for level4
      const thirdOption = secondOption.options[0]
      if (Array.isArray(thirdOption.options) && thirdOption.options.length > 0) {
        levels.push('level4')

        // Check for level5
        const fourthOption = thirdOption.options[0]
        if (Array.isArray(fourthOption.options) && fourthOption.options.length > 0) {
          levels.push('level5')
        }
      }
    }
  }

  return levels
}

export const CascadingField = ({ field, formFieldName, control, variant }: BaseFieldProps) => {
  // Infer levels from the options structure
  const levels = useMemo(() => inferLevelsFromOptions(field.options), [field.options])

  // Get the field labels from the schema or use defaults
  const fieldLabels = useMemo(() => field.fieldLabels || {}, [field.fieldLabels])

  // Track options for each level
  const [levelOptions, setLevelOptions] = useState<Record<string, CascadingOption[]>>({})

  // Store the current form value for use in effects
  const [currentValue, setCurrentValue] = useState<CascadingFieldValue>({})
  const isMountedRef = useRef(false)

  // Set mounted flag
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Initialize first level options
  useEffect(() => {
    if (levels.length > 0) {
      const firstLevelKey = levels[0]
      const newOptions = Array.isArray(field.options) ? field.options : []

      setLevelOptions((prev) => {
        // Only update if options have changed
        const currentOptions = prev[firstLevelKey]

        if (JSON.stringify(currentOptions) === JSON.stringify(newOptions)) {
          return prev
        }

        return {
          ...prev,
          [firstLevelKey]: newOptions,
        }
      })
    }
  }, [field.options, levels])

  // Update options for each level when parent level selection changes
  useEffect(() => {
    // Skip the first level as it's already initialized
    for (let i = 1; i < levels.length; i++) {
      const parentLevel = levels[i - 1]
      const currentLevel = levels[i]

      if (currentValue[parentLevel]) {
        const parentOptions = levelOptions[parentLevel] || []
        const selectedParent = parentOptions.find((option) => option.value === currentValue[parentLevel])

        if (selectedParent?.options) {
          const newOptions = Array.isArray(selectedParent.options) ? selectedParent.options : []

          setLevelOptions((prevOptions) => {
            // Only update if options have changed
            if (JSON.stringify(prevOptions[currentLevel]) === JSON.stringify(newOptions)) {
              return prevOptions
            }

            return {
              ...prevOptions,
              [currentLevel]: newOptions,
            }
          })
        } else if (levelOptions[currentLevel]?.length > 0) {
          // Clear child options when parent has no options
          setLevelOptions((prevOptions) => ({
            ...prevOptions,
            [currentLevel]: [],
          }))
        }
      } else if (levelOptions[currentLevel]?.length > 0) {
        // Clear child options when parent is not selected
        setLevelOptions((prevOptions) => ({
          ...prevOptions,
          [currentLevel]: [],
        }))
      }
    }
  }, [currentValue, levels, levelOptions])

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        // Ensure we have a valid value object
        const value = formField.value || ({} as CascadingFieldValue)

        // Update the current value if it's different from the form value
        // This is safe because it doesn't directly update state during render
        if (isMountedRef.current && JSON.stringify(value) !== JSON.stringify(currentValue)) {
          // Use requestAnimationFrame to defer the state update until after render
          requestAnimationFrame(() => {
            setCurrentValue(value)
          })
        }

        // Handle changes to a level
        const handleLevelChange = (levelKey: string, levelValue: string | string[]) => {
          const newValue = { ...value, [levelKey]: levelValue }

          // Clear all child levels
          const levelIndex = levels.indexOf(levelKey)
          if (levelIndex >= 0) {
            for (let i = levelIndex + 1; i < levels.length; i++) {
              const childLevel = levels[i]
              const isMultiSelect = field.isMultiSelect?.[childLevel as keyof typeof field.isMultiSelect]
              newValue[childLevel] = isMultiSelect ? [] : ''
            }
          }

          formField.onChange(newValue)
        }

        return (
          <FormItem className="flex w-full flex-col space-y-2">
            <FormLabel className={variant === 'row' ? 'sr-only' : 'block'}>{field.title}</FormLabel>

            <div className={cn('flex flex-col space-y-2', variant === 'row' && '!mt-0 flex-row space-x-2 space-y-0')}>
              {/* Dynamically render comboboxes for each level */}
              {levels.map((levelKey: string, index: number) => {
                // Fix the shouldShow logic - it should be a boolean, not a string or empty value
                const shouldShow =
                  index === 0 ||
                  (!!value[levels[index - 1]] && levelOptions[levelKey] && levelOptions[levelKey].length > 0)

                const levelValue = value[levelKey]
                const isMultiSelect = field.isMultiSelect?.[levelKey as keyof typeof field.isMultiSelect]
                const levelLabel = fieldLabels[levelKey as keyof typeof fieldLabels] || `Level ${index + 1}`

                // Get options for this level and ensure it's an array
                const options = levelOptions[levelKey] || []

                // Convert options to the format expected by the combobox components
                const comboboxOptions = options.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))

                if (!shouldShow) {
                  return null // Don't render this level if it shouldn't be shown
                }

                // Force a key change when options change to ensure the component re-renders
                const optionsKey = JSON.stringify(comboboxOptions)
                const componentKey = `${levelKey}-${optionsKey}`

                return (
                  <div key={componentKey} className="flex-1">
                    {isMultiSelect ? (
                      <MultiLevelCombobox
                        options={comboboxOptions}
                        value={Array.isArray(levelValue) ? levelValue : []}
                        onChange={(newValue) => handleLevelChange(levelKey, newValue)}
                        placeholder={`Select ${levelLabel.toLowerCase()}`}
                        disabled={!shouldShow}
                        variant={variant}
                      />
                    ) : (
                      <SingleLevelCombobox
                        options={comboboxOptions}
                        value={typeof levelValue === 'string' ? levelValue : ''}
                        onChange={(newValue) => handleLevelChange(levelKey, newValue)}
                        placeholder={`Select ${levelLabel.toLowerCase()}`}
                        disabled={!shouldShow}
                        variant={variant}
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {variant === 'column' && <FormDescription>{field.description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
